import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
// Direct imports (not barrel) — the visual-edits babel plugin trips on
// top-level barrel `ImportDeclaration`s when tracing cross-file props.
import { Navigation } from '@/components/wedding/Navigation';
import { Footer } from '@/components/wedding/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Same master monogram used across the entire site — softer champagne
// `#C8A951`, the canonical brand accent. Header and footer render the
// identical file so spacing, proportions and color match technically
// regardless of the surface behind them.
const FOOTER_MONOGRAM = `${BACKEND_URL}/api/monogram/gold-champagne.svg`;
const FALLBACK_MOTTO = 'Grace Made Us. Purpose Unites Us.';

/**
 * Compact footer variant — a centered brand cluster (monogram + motto) with
 * a hairline copyright row. Used on every non-home wedding page so the
 * refined M&A mark carries through the entire site alongside the couple's
 * motto pulled from the Home Hero CMS.
 */
const MinimalFooter = () => {
  const [motto, setMotto] = useState(FALLBACK_MOTTO);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/hero/content`, { params: { _: Date.now() } });
        if (!alive) return;
        if (res.data?.motto) setMotto(res.data.motto);
      } catch { /* keep fallback */ }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <footer
      className="border-t border-[#3B2F2F]/10 bg-[#F8F5EF] pt-10 pb-4"
      data-testid="minimal-footer"
    >
      <div className="max-w-6xl mx-auto px-4">
        {/* Master M&A monogram — same refined mark as the top-left nav and
            the invitation suite. Kept modest in size so the footer stays
            light but the brand cue lands. */}
        <Link
          to="/"
          className="flex flex-col items-center justify-center gap-3"
          aria-label="Mary & Arrington — home"
        >
          <img
            src={FOOTER_MONOGRAM}
            alt="Mary & Arrington monogram"
            className="h-10 sm:h-12 md:h-14 w-auto select-none"
            draggable="false"
            data-testid="footer-monogram"
          />
          {/* Motto — pulled from the Home Hero CMS so edits ripple everywhere. */}
          <p
            className="text-[#5A4A4A]/85 text-[0.65rem] sm:text-xs tracking-[0.28em] uppercase font-serif text-center"
            data-testid="footer-motto"
          >
            {motto}
          </p>
        </Link>

        {/* Hairline copyright row */}
        <div className="mt-8 pt-4 border-t border-[#3B2F2F]/10 text-center text-[#5A4A4A]/60 text-[0.65rem] tracking-[0.18em] uppercase font-serif">
          © 2027 The Johnsons
        </div>
      </div>
    </footer>
  );
};

/**
 * Shared layout for every non-home wedding page.
 * - Renders the fixed Navigation (solid on sub-pages)
 * - Reserves top spacing so section content isn't hidden under the nav
 * - Renders a footer variant at the bottom
 * - Scrolls back to the top when landing on the page
 *
 * @param {'full'|'minimal'|'none'} [footer='minimal']
 */
const PageLayout = ({ children, testId, footer = 'minimal' }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F5EF]" data-testid={testId}>
      <div className="grain-overlay" />
      <Navigation />
      <main className="pt-20 sm:pt-24">
        {children}
      </main>
      {footer === 'full' && <Footer />}
      {footer === 'minimal' && <MinimalFooter />}
    </div>
  );
};

export default PageLayout;
